import { MUIButton } from "@abb/arcadia-ui-react"
import { useCallback, useState } from "react"
import { useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { useApplicationUsers } from "../../../../../hooks/useApplicationUsers"
import { digitalManagerApi } from "../../../../../services/api"
import { ApplicationUserGet } from "../../../../models/ApplicationUser"
import { downloadBlob } from "../../../../utils/downloadBlob"
import {
  dateStringComparator,
  getNiceFormattedDate,
} from "../../../../utils/getFormattedDate"
import { EntityTab } from "../../../partials/EntityTab"

export function ApplicationUsers() {
  const { applicationId } = useParams()
  const { applicationUsers, isLoadingApplicationUsers } =
    useApplicationUsers(applicationId)
  const [isNpsDownloading, setIsNpsDownloading] = useState(false)

  const downloadNps = useCallback(() => {
    setIsNpsDownloading(true)
    digitalManagerApi
      .get<Blob>(`/applications/${applicationId}/users/export`, { responseType: "blob" })
      .then((res) => {
        const tempDate = new Date()
        const [day, month, year] = [
          tempDate.getDate(),
          tempDate.getMonth() + 1,
          tempDate.getFullYear(),
        ]
        const fileName = `NPS-${year}_${month}_${day}.csv`
        downloadBlob(res.data, fileName)
      })
      .catch(() => toast.error("Cannot download the NPS file"))
      .finally(() => setIsNpsDownloading(false))
  }, [applicationId])

  const NpsToolbar = useCallback(
    () => (
      <div
        style={{
          alignItems: "center",
          display: "flex",
          gap: "48px",
        }}
      >
        <MUIButton
          iconName="icon-abb_setting icon_abb_dias"
          label="Export NPS"
          size={32}
          variant="primary"
          isLoading={isNpsDownloading}
          disabled={isNpsDownloading}
          onClickButton={downloadNps}
        />
      </div>
    ),
    [downloadNps, isNpsDownloading]
  )

  const columns = [
    { field: "username", headerName: "Email address" },
    { field: "fullName", headerName: "Full name" },
    {
      field: "lastAccess",
      headerName: "Last access",
      sortComparator: dateStringComparator,
    },
    { field: "version", headerName: "Version" },
    { field: "operatingSystem", headerName: "OS" },
  ]

  const sorted = [...applicationUsers].sort((a, b) =>
    (a.user?.username ?? "").localeCompare(b.user?.username ?? "")
  )

  const rows = sorted.map((user: ApplicationUserGet) => ({
    id: user.id,
    username: user.user.username,
    fullName: `${user.user.first ?? ""} ${user.user.last ?? ""}`.trim(),
    lastAccess: getNiceFormattedDate(user.lastAccess),
    version: user.version ?? "",
    operatingSystem: user.operatingSystem ?? "",
  }))

  return (
    <EntityTab
      rows={rows}
      cols={columns}
      isLoading={isLoadingApplicationUsers}
      toolbar={<NpsToolbar />}
    />
  )
}
